import { useCallback } from 'react';
import useStores from '@/lib/hooks/useStores';
import { Cart, CartType } from '@shared/types/cart/Cart';
import { sdk } from '@/sdk';
import useSWR from 'swr';
import { Address } from '@shared/types/account/Address';
import { Order } from '@shared/types/cart';
import { calculateTransaction } from '@/lib/utils/calculate-transaction';
import { ApiResult } from '@/types/lib/api';
import { Product } from '@/types/entity/product';
import { CheckoutPayload, QuoteRequestPayload } from './types';
import { useProducts } from '../useProduct';

const useCart = (businessUnitKey?: string, storeKey?: string) => {
  const { channel: storeChannelVisibility, priceChannel: storeChannelPrice } = useStores();

  const getCart = useCallback(async () => {
    const result = await sdk.composableCommerce.cart.getCart({
      businessUnitKey: businessUnitKey as string,
      storeKey: storeKey as string,
    });

    return result.isError ? ({} as Cart) : result.data;
  }, [businessUnitKey, storeKey]);

  const { data, isLoading, mutate } = useSWR(
    !(businessUnitKey && storeKey) ? null : ['/action/cart/getCart', businessUnitKey, storeKey],
    getCart,
  );

  const productIds =
    data?.lineItems?.reduce<string[]>((acc, item) => {
      if (item.productId) acc.push(item.productId);
      return acc;
    }, []) || [];

  const checkIsPreOrderReady = !isLoading && typeof data?.isPreOrder !== 'undefined';
  const productIdsReady = productIds.length > 0;

  const products = useProducts(
    {
      productIds,
      isCommercialOperation: data?.isPreOrder,
      customerCategoryChannelKey: storeChannelVisibility?.key,
      distributionChannelId: storeChannelPrice?.channelId,
    },
    productIdsReady && checkIsPreOrderReady,
  );

  const isQuotationCart = data?.origin === 'Quote';

  const addItem = useCallback(
    async (
      lineItems: Array<{ sku: string; count: number; isPreOrder?: boolean; commercialOperationKey?: string | null }>,
      channelId?: string,
    ): Promise<ApiResult<Cart>> => {
      const payload = {
        lineItems: lineItems.map(({ sku, count, isPreOrder, commercialOperationKey }) => ({
          variant: { sku },
          count,
          ...(isPreOrder !== undefined ? { isPreOrder } : {}),
          ...(commercialOperationKey ? { commercialOperationKey } : {}),
        })),
        businessUnitKey,
      };

      const result = await sdk.composableCommerce.cart.addItem(payload, {
        businessUnitKey: businessUnitKey as string,
        storeKey: storeKey as string,
        distributionChannelId: channelId,
      });

      if (!result.isError) await mutate(result.data, { revalidate: false });

      return result.isError ? { success: false, message: result.error.message } : { ...result.data, success: true };
    },
    [mutate, businessUnitKey, storeKey],
  );

  const updateItem = useCallback(
    async (lineItem: { id: string; count: number }) => {
      const payload = { lineItem, businessUnitKey };

      const result = await sdk.composableCommerce.cart.updateItem(payload, {
        businessUnitKey: businessUnitKey as string,
        storeKey: storeKey as string,
      });

      if (!result.isError) await mutate(result.data, { revalidate: false });

      return result.isError ? {} : result.data;
    },
    [mutate, businessUnitKey, storeKey],
  );

  const removeItem = useCallback(
    async (lineItemId: string) => {
      const payload = { lineItem: { id: lineItemId }, businessUnitKey };

      const result = await sdk.composableCommerce.cart.removeItem(payload, {
        businessUnitKey: businessUnitKey as string,
        storeKey: storeKey as string,
      });

      if (!result.isError) await mutate(result.data, { revalidate: false });

      return result.isError ? {} : result.data;
    },
    [mutate, businessUnitKey, storeKey],
  );

  const requestQuote = useCallback(
    async (payload: QuoteRequestPayload) => {
      const result = await sdk.composableCommerce.quote.createQuote(
        {
          orderReference: payload.orderReference,
          wishedDeliveryDate: payload.wishedDeliveryDate,
          isPreOrder: payload.isPreOrder,
          cartLabel: payload.cartLabel,
          orderCustomerComment: payload.orderCustomerComment,
          orderShippingMethod: payload.orderShippingMethod,
          orderNature: payload.orderNature,
        },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );

      await mutate();

      return result.isError ? {} : result.data;
    },
    [mutate, businessUnitKey, storeKey],
  );

  const updateCart = useCallback(
    async ({ shipping, billing, email }: { shipping?: Address; billing?: Address; email?: string }) => {
      const result = await sdk.composableCommerce.cart.updateCart(
        { shipping, billing, ...(email ? { account: { email } } : {}) },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );

      if (!result.isError) await mutate(result.data, { revalidate: false });

      return result.isError ? { success: false, error: result.error } : { ...result.data, success: true };
    },
    [mutate, businessUnitKey, storeKey],
  );

  const redeemDiscount = useCallback(
    async (code: string) => {
      const result = await sdk.composableCommerce.cart.redeemDiscountCode(
        { code },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );

      if (!result.isError) await mutate(result.data, { revalidate: false });

      return result.isError ? ({} as Partial<Cart>) : result.data;
    },
    [mutate, businessUnitKey, storeKey],
  );

  const removeDiscount = useCallback(
    async (discountId: string) => {
      const result = await sdk.composableCommerce.cart.removeDiscountCode(
        { discountId },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );

      if (!result.isError) await mutate(result.data, { revalidate: false });

      return result.isError ? ({} as Partial<Cart>) : result.data;
    },
    [mutate, businessUnitKey, storeKey],
  );

  const clearCart = useCallback(async () => {
    await sdk.composableCommerce.cart.clearCart();

    await mutate();
  }, [mutate]);

  const checkout = useCallback(
    async ({ purchaseOrderNumber }: CheckoutPayload) => {
      const result = await sdk.composableCommerce.cart.checkout(
        { purchaseOrderNumber },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );

      await mutate();

      return result.isError ? ({} as Partial<Order>) : result.data;
    },
    [mutate, businessUnitKey, storeKey],
  );

  const getShippingMethods = useCallback(async () => {
    const result = await sdk.composableCommerce.cart.getShippingMethods({
      businessUnitKey,
      storeKey,
      onlyMatching: true,
    });

    return result.isError ? [] : result.data;
  }, [businessUnitKey, storeKey]);

  const { data: shippingMethods } = useSWR(
    businessUnitKey && storeKey ? ['/action/cart/getShippingMethods', businessUnitKey, storeKey] : null,
    getShippingMethods,
  );

  const setShippingMethod = useCallback(
    async (shippingMethodId: string) => {
      const result = await sdk.composableCommerce.cart.setShippingMethod(
        { shippingMethod: { id: shippingMethodId } },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );

      if (!result.isError) await mutate(result.data, { revalidate: false });

      return result.isError ? ({} as Partial<Cart>) : result.data;
    },
    [businessUnitKey, storeKey, mutate],
  );

  const replicateOrder = async (items: Partial<Product>[], isPreOrder: boolean, cartLabel: string) => {
    await clearCart();

    const addResp = await addItem(
      items.map((i) => ({
        sku: i.sku ?? '',
        count: i.quantity ?? 0,
        isPreOrder: i.isPreOrder ?? false,
        ...(i.isPreOrder && i.commercialOperationKey ? { commercialOperationKey: i.commercialOperationKey } : {}),
      })),
      storeChannelPrice?.channelId,
    );

    if (!addResp.success) return addResp;

    await updateCustomField('cartType', computeCartType(items as Product[]));
    await updateCustomField('isPreOrder', isPreOrder);
    await updateCustomField('label', cartLabel ?? '');

    return addResp;
  };

  const mergeOrder = async (items: Partial<Product>[]) => {
    return addItem(
      items.map((i) => ({
        sku: i.sku ?? '',
        count: i.quantity ?? 0,
        isPreOrder: i.isPreOrder ?? false,
        ...(i.isPreOrder && i.commercialOperationKey ? { commercialOperationKey: i.commercialOperationKey } : {}),
      })),
      storeChannelPrice?.channelId,
    );
  };

  const updateCustomField = useCallback(
    async (name: string, value: boolean | string) => {
      const result = await sdk.composableCommerce.cart.updateCartCustomField(
        { name, value },
        { businessUnitKey: businessUnitKey as string, storeKey: storeKey as string },
      );

      if (!result.isError) await mutate(result.data, { revalidate: false });

      return result.isError ? { success: false, error: result.error } : { ...result.data, success: true };
    },
    [mutate, businessUnitKey, storeKey],
  );

  const computeCartType = useCallback((items: Product[]): CartType => {
    const hasBox = items.some((i) => i.specifications?.find((s) => s.label === 'type')?.value === 'box');
    const hasPreOrder = items.some((i) => i.isPreOrder);
    const hasMD = items.some((i) => i.isDangerousMaterial);

    if (hasPreOrder && hasBox && hasMD) return CartType.PreOrderBoxMD;
    if (hasPreOrder && hasBox) return CartType.PreOrderBox;
    if (hasPreOrder && hasMD) return CartType.PreOrderSimpleMD;
    if (hasPreOrder) return CartType.PreOrderSimple;
    if (hasBox && hasMD) return CartType.ReassortBoxMD;
    if (hasBox) return CartType.ReassortBox;
    if (hasMD) return CartType.ReassortSimpleMD;
    return CartType.ReassortSimple;
  }, []);

  return {
    cart: data ? { ...data, transaction: calculateTransaction(data) } : undefined,
    products,
    isQuotationCart,
    shippingMethods,
    totalItems: data?.lineItems?.reduce((acc, curr) => acc + (curr.count ?? 1), 0) ?? 0,
    isLoading,
    addItem,
    updateItem,
    removeItem,
    requestQuote,
    updateCart,
    setShippingMethod,
    redeemDiscount,
    removeDiscount,
    checkout,
    clearCart,
    replicateOrder,
    mergeOrder,
    updateCustomField,
    computeCartType,
  };
};

export default useCart;
